import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import api from '../api/axios'
import { formatImageUrl } from '../utils/formatImageUrl'

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try { return new Date(dateStr).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return dateStr; }
};

const getImage = (img) => {
    return formatImageUrl(img, '/assets/images/thumbs/blog-img1.png');
};

const BlogDetails = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const { slug } = useParams()

    const [post, setPost] = useState(location.state?.post || null)
    const [related, setRelated] = useState([])
    const [loading, setLoading] = useState(!post)

    // Fetch by slug from API if not passed via state
    useEffect(() => {
        if (post) {
            // fetch all for related
            api.get('/api/blogs').then(res => {
                setRelated((res.data || []).filter(p => p.id !== post.id && p.slug !== post.slug).slice(0, 3));
            }).catch(() => { });
            return;
        }

        const fetchBySlug = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/api/blogs/${slug}`);
                const fetchedPost = res.data;
                setPost({
                    ...fetchedPost,
                    image: getImage(fetchedPost.image),
                    date: formatDate(fetchedPost.date || fetchedPost.createdAt),
                    readTime: fetchedPost.readTime || '4 min read',
                    category: fetchedPost.category || 'Healthy Living',
                });

                // fetch related
                const allRes = await api.get('/api/blogs');
                setRelated((allRes.data || []).filter(p => p.id !== fetchedPost.id).slice(0, 3));
            } catch {
                navigate('/blog');
            }
            setLoading(false);
        };

        if (slug) fetchBySlug();
    }, [slug]);

    if (loading) {
        return <div className="flex-center py-80"><p className="text-gray-500">Loading blog...</p></div>;
    }

    if (!post) return null;

    return (
        <section className="blog-details-page">
            {/* Hero Header */}
            <div
                className="blog-hero d-flex flex-column align-items-center justify-content-center text-center px-16"
                style={{ backgroundColor: '#E6F9EF', padding: '56px 16px 44px' }}
            >
                <span
                    className="d-inline-flex align-items-center gap-8 fw-semibold text-uppercase mb-16"
                    style={{ letterSpacing: '1px', fontSize: 13, color: '#2FA84F' }}
                >
                    <i className="ph ph-leaf" />
                    {post.category}
                </span>
                <h2 className="text-black mb-0" style={{ maxWidth: 760, fontWeight: 700, lineHeight: 1.3, fontSize: 'clamp(24px, 4vw, 38px)' }}>
                    {post.title}
                </h2>
            </div>

            <div className="container container-lg" style={{ maxWidth: 760 }}>
                {/* Featured Image */}
                <div className="blog-featured-img position-relative" style={{ marginTop: 'clamp(-30px, -4vw, -40px)' }}>
                    <img
                        src={getImage(post.image)}
                        alt={post.title}
                        className="w-100 rounded-16"
                        style={{ objectFit: 'cover', maxHeight: 420, boxShadow: '0 20px 40px rgba(15,36,56,0.15)' }}
                    />
                </div>

                {/* Meta row */}
                <div className="flex-align flex-wrap gap-24 mt-24 mb-32 pb-24 border-bottom border-gray-100">
                    <div className="flex-align gap-8">
                        <i className="ph ph-calendar-dots text-main-600" />
                        <span className="text-sm text-gray-500">{post.date}</span>
                    </div>
                    <div className="flex-align gap-8">
                        <i className="ph ph-clock text-main-600" />
                        <span className="text-sm text-gray-500">{post.readTime || '4 min read'}</span>
                    </div>
                    {post.author && (
                        <div className="flex-align gap-8">
                            <i className="ph ph-user text-main-600" />
                            <span className="text-sm text-gray-500">{post.author}</span>
                        </div>
                    )}
                    <div className="flex-align gap-8">
                        <span className="bg-main-50 text-main-600 py-4 px-16 rounded-8 text-xs fw-semibold">{post.category}</span>
                    </div>
                </div>

                {/* Article content */}
                <article className="blog-article-content" style={{ fontSize: 17, lineHeight: 1.85, color: '#3E4B54' }}>
                    {post.excerpt && (
                        <p className="mb-24">
                            <strong className="text-neutral-800">{post.excerpt}</strong>
                        </p>
                    )}
                    {post.content ? (
                        <div style={{ whiteSpace: 'pre-wrap' }}>{post.content}</div>
                    ) : (
                        <p className="mb-24 text-gray-500 fst-italic">No content available for this post.</p>
                    )}
                </article>

                {/* Tags */}
                {Array.isArray(post.tags) && post.tags.length > 0 && (
                    <div className="flex-align flex-wrap gap-8 mt-32 mb-16">
                        {post.tags.map((tag, i) => (
                            <span key={i} className="bg-main-50 text-main-600 py-4 px-16 rounded-8 text-xs fw-semibold">{tag}</span>
                        ))}
                    </div>
                )}

                {/* Back button */}
                <div className="flex-align flex-wrap justify-content-end gap-16 py-24 border-top border-bottom border-gray-100 mb-40">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex-align gap-8 text-sm fw-semibold text-gray-600 bg-transparent border-0"
                    >
                        <i className="ph ph-arrow-left" />
                        Back to Blog
                    </button>
                </div>

                {/* Related Blogs */}
                {related.length > 0 && (
                    <div className="mb-64">
                        <h6 className="mb-24">Related Blog Posts</h6>
                        <div className="d-flex flex-column gap-16">
                            {related.map((r) => (
                                <Link
                                    key={r.id}
                                    to={`/blog/${r.slug}`}
                                    state={{ post: { ...r, image: getImage(r.image), date: formatDate(r.date || r.createdAt), category: r.category || 'Healthy Living' } }}
                                    className="flex-align gap-16 p-12 rounded-12 border border-gray-100 hover-border-main-600"
                                    style={{ textDecoration: 'none' }}
                                >
                                    <img src={getImage(r.image)} alt={r.title} className="rounded-8 flex-shrink-0" style={{ width: 72, height: 72, objectFit: 'cover' }} />
                                    <div className="min-w-0">
                                        <span className="bg-main-50 text-main-600 py-2 px-12 rounded-8 text-xs fw-semibold d-inline-block mb-4">{r.category || 'Healthy Living'}</span>
                                        <p className="text-sm fw-semibold text-neutral-800 text-line-2 mb-0">{r.title}</p>
                                        <span className="text-xs text-gray-500">{formatDate(r.date || r.createdAt)}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    )
}

export default BlogDetails